import { FC, useEffect, useState } from "react";
import { Alert, Button, Col, Input, Row, Table, Typography, message } from "antd";
import { ImportOutlined } from "@ant-design/icons";

import Papa from "papaparse";

import {
    DealAnalysisOutput,
    DynamicLever,
    DynamicLeverEvaluation,
} from "../../../../types/ProjectTypes";
import { parseEnum } from "../../../../common/Enum";
import { convertGoogleSheet } from "../../../../common/api/ReportClient";
import { handleError } from "../../../../common/ErrorHandling";
import { GoogleShareHint } from "./GoogleShareHint";

// --- CSV parsing ---

interface ParsedCSVResult {
    levers: DynamicLever[];
    orderScore?: number;
    optimizedScore?: number;
    errors: string[];
}

function parsePercentage(val: string): number | undefined {
    const match = val.match(/(\d+(?:\.\d+)?)\s*%?/);
    return match ? parseFloat(match[1]) : undefined;
}

function parsePriority(val: string): number | undefined {
    const match = val.match(/\d+/);
    return match ? parseInt(match[0], 10) : undefined;
}

function parseYesNo(val: string): boolean {
    const v = val.trim().toUpperCase();
    if (v === "Y") return true;
    if (v === "N") return false;
    throw new Error(`Invalid value "${val}". Expected "Y" or "N".`);
}

function parseEvaluation(val: string): DynamicLeverEvaluation {
    return parseEnum(DynamicLeverEvaluation, val.trim());
}

function isEmptyRow(row: string[]): boolean {
    return row.every((cell) => !cell.trim());
}

function isHeaderRow(row: string[]): boolean {
    const lower = row.map((c) => c.trim().toLowerCase());
    return lower.includes("priority") && lower.includes("lever");
}

function parseLeverRow(
    row: string[],
    extra: Pick<DynamicLever, "isActionable"> & Partial<DynamicLever>
): DynamicLever {
    // columns: [empty, priority, name, type, included, evaluation, currentState, optimalState, justification]
    return {
        priority: parsePriority(row[1]),
        name: row[2]?.trim(),
        type: row[3]?.trim(),
        presentInContract: parseYesNo(row[4]),
        evaluation: parseEvaluation(row[5]),
        currentState: row[6]?.trim(),
        optimalState: row[7]?.trim(),
        justification: row[8]?.trim(),
        ...extra,
    };
}

function parseCSVDynamicLevers(csvText: string): ParsedCSVResult {
    const { data: rows } = Papa.parse<string[]>(csvText, { header: false });
    const result: ParsedCSVResult = { levers: [], errors: [] };

    let inLeversSection = false;
    let isActionable = null;

    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];

        // Check for section headers
        if (row.some((cell) => cell?.includes("Levers Requiring Attention"))) {
            isActionable = true;
            continue;
        }
        if (row.some((cell) => cell?.includes("Levers in Good Standing"))) {
            isActionable = false;
            continue;
        }

        // section starts with header row
        if (isHeaderRow(row)) {
            inLeversSection = true;
            continue;
        }

        // section ends with empty row
        if (isEmptyRow(row)) {
            isActionable = null;
            continue;
        }

        if (inLeversSection && isActionable !== null) {
            try {
                result.levers.push(parseLeverRow(row, { isActionable }));
            } catch (e: any) {
                const name = row[2]?.trim();
                result.errors.push(`Row ${i + 1}${name ? ` (${name})` : ""}: ${e.message}`);
            }
        } else {
            const key = row[1]?.trim().toLowerCase();
            if (key === "current score") {
                result.orderScore = parsePercentage(row[2] || "");
            } else if (key === "optimal score") {
                result.optimizedScore = parsePercentage(row[2] || "");
            }
        }
    }

    return result;
}

interface LeversTabProps {
    output: DealAnalysisOutput;
    onChange: (output: DealAnalysisOutput) => void;
}

/**
 * Import levers from the Google sheet and save them on DealAnalysisOutput.
 */
export const LeversTab: FC<LeversTabProps> = ({ output, onChange }) => {
    const levers = output.levers ?? [];
    const dealInfo = output.dealInfo;
    const [googleSheetUrl, setGoogleSheetUrl] = useState("");
    const [isImportingSheet, setIsImportingSheet] = useState(false);
    const [importErrors, setImportErrors] = useState<string[]>([]);

    useEffect(() => {
        setGoogleSheetUrl(output.sourceUrls?.levers ?? "");
    }, [output]);

    const applyImportResult = (result: ParsedCSVResult) => {
        const messages: string[] = [];
        const updates: Partial<DealAnalysisOutput> = {};

        if (result.levers.length > 0) {
            updates.levers = result.levers;
            messages.push(`${result.levers.length} levers`);
        }

        if (result.orderScore !== undefined || result.optimizedScore !== undefined) {
            updates.dealInfo = {
                ...dealInfo!,
                ...(result.orderScore !== undefined && { orderScore: result.orderScore }),
                ...(result.optimizedScore !== undefined && {
                    optimizedScore: result.optimizedScore,
                }),
            };
            if (result.orderScore !== undefined)
                messages.push(`order score: ${result.orderScore}%`);
            if (result.optimizedScore !== undefined)
                messages.push(`optimized score: ${result.optimizedScore}%`);
        }

        if (Object.keys(updates).length > 0) {
            onChange({
                ...output,
                ...updates,
                sourceUrls: { ...output.sourceUrls, levers: googleSheetUrl.trim() },
            });
        }

        return messages;
    };

    const handleSheetImport = async () => {
        if (!googleSheetUrl.trim()) {
            message.warning("Please enter a Google Sheet URL or File ID");
            return;
        }
        setIsImportingSheet(true);
        setImportErrors([]);
        try {
            const response = await convertGoogleSheet(googleSheetUrl.trim());
            const csv = (response as any).data?.csv || (response as any).csv;
            if (csv) {
                const result = parseCSVDynamicLevers(csv);
                setImportErrors(result.errors);
                const messages = applyImportResult(result);
                if (messages.length > 0) {
                    message.success(`Imported ${messages.join(", ")} from Google Sheet`);
                } else if (result.errors.length === 0) {
                    message.warning("No levers or scores found in Google Sheet");
                }
            } else {
                message.warning("No CSV data found in Google Sheet response");
            }
        } catch (error) {
            handleError(error);
        } finally {
            setIsImportingSheet(false);
        }
    };

    const leversWithIndex = levers.map((lever, index) => ({ lever, index }));
    const attentionLevers = leversWithIndex.filter(({ lever }) => lever.isActionable);
    const inGoodStandingLevers = leversWithIndex.filter(({ lever }) => !lever.isActionable);

    const columns = [
        { title: "Priority", dataIndex: ["lever", "priority"], key: "priority", width: 35 },
        { title: "Name", dataIndex: ["lever", "name"], key: "name", width: 150 },
        { title: "Type", dataIndex: ["lever", "type"], key: "type", width: 90 },
        {
            title: "isActionable",
            dataIndex: ["lever", "isActionable"],
            key: "isActionable",
            width: 40,
            render: (v: boolean) => (v ? "Y" : "N"),
        },
        {
            title: "Included?",
            dataIndex: ["lever", "presentInContract"],
            key: "presentInContract",
            width: 40,
            render: (v: boolean) => (v ? "Y" : "N"),
        },
        { title: "Evaluation", dataIndex: ["lever", "evaluation"], key: "evaluation", width: 95 },
        { title: "Current state", dataIndex: ["lever", "currentState"], key: "currentState" },
        { title: "Optimal state", dataIndex: ["lever", "optimalState"], key: "optimalState" },
        { title: "Justification", dataIndex: ["lever", "justification"], key: "justification" },
    ];

    const renderTable = (data: { lever: DynamicLever; index: number }[]) => (
        <Table
            dataSource={data.map((item) => ({ ...item, key: item.index }))}
            columns={columns}
            pagination={false}
            size="small"
            scroll={{ x: 1200 }}
        />
    );

    return (
        <div>
            {/* Google Sheet import */}
            <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col flex="auto">
                    <Input
                        placeholder="Enter Google Sheet URL or File ID"
                        value={googleSheetUrl}
                        onChange={(e) => setGoogleSheetUrl(e.target.value)}
                        onPressEnter={handleSheetImport}
                        disabled={isImportingSheet}
                    />
                </Col>
                <Col>
                    <Button
                        icon={<ImportOutlined />}
                        onClick={handleSheetImport}
                        loading={isImportingSheet}
                    >
                        Import
                    </Button>
                </Col>
            </Row>
            <GoogleShareHint />

            {/* Import Errors */}
            {importErrors.length > 0 && (
                <Alert
                    type="error"
                    style={{ marginBottom: 8 }}
                    message="Import errors — please fix the sheet and re-import"
                    description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {importErrors.map((err, i) => (
                                <li key={i}>{err}</li>
                            ))}
                        </ul>
                    }
                    closable
                    onClose={() => setImportErrors([])}
                />
            )}

            {/* Deal Scores */}
            {(dealInfo?.orderScore != null || dealInfo?.optimizedScore != null) && (
                <Row gutter={24} style={{ marginBottom: 16 }}>
                    {dealInfo?.orderScore != null && (
                        <Col>
                            <Typography.Text strong>Current score: </Typography.Text>
                            <Typography.Text>{dealInfo.orderScore}%</Typography.Text>
                        </Col>
                    )}
                    {dealInfo?.optimizedScore != null && (
                        <Col>
                            <Typography.Text strong>Optimal score: </Typography.Text>
                            <Typography.Text>{dealInfo.optimizedScore}%</Typography.Text>
                        </Col>
                    )}
                </Row>
            )}

            {/* Levers Requiring Attention */}
            {attentionLevers.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <Typography.Title level={5} style={{ marginBottom: 12 }}>
                        Levers requiring attention ({attentionLevers.length})
                    </Typography.Title>
                    {renderTable(attentionLevers)}
                </div>
            )}

            {/* Levers in Good Standing */}
            {inGoodStandingLevers.length > 0 && (
                <div>
                    <Typography.Title level={5} style={{ marginBottom: 12 }}>
                        Levers in good standing ({inGoodStandingLevers.length})
                    </Typography.Title>
                    {renderTable(inGoodStandingLevers)}
                </div>
            )}
        </div>
    );
};
